// Considered: ArchetypesFieldMolecule — rejected because it shows and edits the Archetype set (picker,
// conflict, 422 problems); the expectation list would give it a second job. FactorSummary summarises
// status factors with health bands, a different data shape. Composes existing atoms only. See spec Reuse Audit.
import React, { useCallback, useId } from 'react';
import { useTranslation } from 'localization';
import { useAnalytics } from '@center-technologies/analytics';
import type { EffectiveExpectedAspect, ExpectedAspectsEvaluation } from 'types/archetype';
import type { ExpectedAspectRow, ExpectedAspectGroup } from '../../../hooks/useExpectedAspectSetup';
import { ATTACHED_GROUP_KEY, NOT_ON_TYPE_GROUP_KEY } from '../../../hooks/useExpectedAspectSetup';
import resolveLocalizedString from '../../../utils/resolveLocalizedString';
import CollapsibleSectionAtom from '../../atoms/CollapsibleSection/CollapsibleSectionAtom';
import TagAtom from '../../atoms/Tag/TagAtom';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import SkeletonAtom from '../../atoms/Skeleton/SkeletonAtom';
import './ExpectedAspectsSummaryMolecule.scss';

export interface ExpectedAspectsSummaryMoleculeProps {
  sphereId: string;
  expectedAspects: EffectiveExpectedAspect[];
  groups: ExpectedAspectGroup[];
  needsAttentionCount: number;
  evaluation?: ExpectedAspectsEvaluation;
  isLoading?: boolean;
  /** Display name of the sphere's type, for the "not part of this type" explanation. */
  typeName?: string;
  /** Heading for a section group; the synthetic groups are titled by this molecule. */
  groupTitle?: (groupKey: string) => string | null;
  onReveal?: (aspectSystemName: string, sectionKey: string) => void;
  onRetry?: () => void;
  /** A Retry re-read is in flight; the button shows its loading state until the answer lands. */
  isRetrying?: boolean;
}

const BLOCK = 'expected-aspects-summary-molecule';

const ExpectedAspectsSummaryMolecule: React.FC<ExpectedAspectsSummaryMoleculeProps> = ({
  sphereId,
  expectedAspects,
  groups,
  needsAttentionCount,
  evaluation,
  isLoading,
  typeName,
  groupTitle,
  onReveal,
  onRetry,
  isRetrying,
}) => {
  const { t, i18n } = useTranslation();
  const logAnalyticsEvent = useAnalytics();
  const idBase = useId();

  const handleReveal = useCallback(
    (row: ExpectedAspectRow) => {
      logAnalyticsEvent('expected_aspect_revealed', undefined, {
        sphereId,
        aspectSystemName: row.aspectSystemName,
        expectation: row.expectation,
        needsAttention: row.needsAttention,
        sectionKey: row.groupKey,
      });
      onReveal?.(row.aspectSystemName, row.groupKey);
    },
    [sphereId, logAnalyticsEvent, onReveal]
  );

  const title = t('archetypes.expected.title');

  // The first read has no expectations yet, so loading is decided before emptiness.
  if (isLoading) {
    return (
      <div className={BLOCK} data-testid="sphere-expected-aspects" aria-busy="true">
        <div className={`${BLOCK}__loading-title`}>{title}</div>
        <SkeletonAtom height="1.5rem" />
      </div>
    );
  }

  if (expectedAspects.length === 0) return null;

  const failed = evaluation === 'failed';

  const headingFor = (groupKey: string): string | null => {
    if (groupKey === NOT_ON_TYPE_GROUP_KEY) return t('archetypes.expected.group.notOnType');
    if (groupKey === ATTACHED_GROUP_KEY) return t('archetypes.expected.group.attached');
    return groupTitle ? groupTitle(groupKey) : null;
  };

  const stateText = (row: ExpectedAspectRow, automatic: boolean): string | null => {
    if (row.placement === 'absent') return t('archetypes.expected.notOnType.body', { type: typeName ?? '' });
    if (row.placement === 'attached') return t('archetypes.expected.attached.body');
    if (failed || row.valueState === null) return null;
    if (row.valueState === 'not_visible') return t('archetypes.expected.state.notVisible');
    const parts = [t(`archetypes.expected.state.${row.valueState}`)];
    if (automatic) parts.push(t('archetypes.expected.state.automatic'));
    if (row.hasWithheld) parts.push(t('archetypes.expected.state.withheld'));
    return parts.join(' · ');
  };

  const renderStatus = () => {
    if (failed) return undefined;
    if (needsAttentionCount > 0) {
      return (
        <TagAtom
          tone="warn"
          size="small"
          icon="pi-exclamation-circle"
          label={t('archetypes.expected.needsAttention', { count: needsAttentionCount })}
          data-testid="sphere-expected-aspects-status"
        />
      );
    }
    return (
      <span className={`${BLOCK}__all-set`} data-testid="sphere-expected-aspects-status">
        {t('archetypes.expected.nothingNeedsAttention')}
      </span>
    );
  };
  const status = renderStatus();

  const expectationTone = (expectation: ExpectedAspectRow['expectation']) => {
    if (expectation === 'required') return 'info';
    return expectation === 'recommended' ? 'neutral' : 'outline';
  };

  const list = (
    <div className={`${BLOCK}__groups`}>
      {groups.map((group) => {
        const heading = headingFor(group.groupKey);
        // The panel sits inside the page's own heading outline, so a group is a labelled list, not a heading.
        const titleId = `${idBase}-${group.groupKey}`;
        return (
          <section key={group.groupKey} className={`${BLOCK}__group`} data-group-key={group.groupKey}>
            {heading && (
              <div id={titleId} className={`${BLOCK}__group-title`}>
                {heading}
              </div>
            )}
            <ul className={`${BLOCK}__rows`} aria-labelledby={heading ? titleId : undefined}>
              {group.rows.map((row) => {
                const name = resolveLocalizedString(row.aspectName, t('archetypes.expected.unnamed'), i18n?.language);
                // What is still missing is read-only or hidden (or the aspect has no properties), so
                // nobody fills it in here and there is nothing to take them to.
                const automatic =
                  !failed &&
                  row.placement === 'composed' &&
                  (row.valueState === 'empty' || row.valueState === 'partial') &&
                  !row.fillable;
                const state = stateText(row, automatic);
                const revealable =
                  row.placement === 'composed' && row.valueState !== 'not_visible' && !automatic && row.hasShownProperty;
                return (
                  <li
                    key={row.aspectSystemName}
                    className={`${BLOCK}__row`}
                    data-expected-aspect={row.aspectSystemName}
                  >
                    <span className={`${BLOCK}__row-name`}>{name}</span>
                    <span className={`${BLOCK}__row-meta`}>
                      <TagAtom
                        tone={expectationTone(row.expectation)}
                        size="small"
                        shape="chip"
                        label={t(`archetypes.expected.expectation.${row.expectation}`)}
                      />
                      {state && (
                        <span
                          className={`${BLOCK}__row-state`}
                          data-needs-attention={row.needsAttention ? 'true' : undefined}
                        >
                          {state}
                        </span>
                      )}
                    </span>
                    <span className={`${BLOCK}__row-reason`}>{row.reasonText}</span>
                    {revealable && (
                      <ButtonAtom
                        type="button"
                        variant="text"
                        size="small"
                        label={t('archetypes.expected.show')}
                        aria-label={t('archetypes.expected.showLabel', { name })}
                        className={`${BLOCK}__show`}
                        onClick={() => handleReveal(row)}
                      />
                    )}
                  </li>
                );
              })}
            </ul>
          </section>
        );
      })}
    </div>
  );

  return (
    <div className={BLOCK} data-testid="sphere-expected-aspects">
      {failed && (
        <output className={`${BLOCK}__failed`}>
          <span className={`${BLOCK}__failed-title`}>{t('archetypes.expected.evaluationFailed.title')}</span>
          <span className={`${BLOCK}__failed-body`}>{t('archetypes.expected.evaluationFailed.body')}</span>
          {onRetry && (
            <ButtonAtom
              type="button"
              variant="text"
              size="small"
              label={t('archetypes.expected.evaluationFailed.retry')}
              loading={isRetrying}
              onClick={onRetry}
            />
          )}
        </output>
      )}
      <CollapsibleSectionAtom
        title={title}
        defaultExpanded={false}
        titleAdornment={status}
        anchorId="sphere-expected-aspects"
        data-testid="sphere-expected-aspects-section"
      >
        {list}
      </CollapsibleSectionAtom>
    </div>
  );
};

export default ExpectedAspectsSummaryMolecule;
